import { useEffect, useMemo, useState } from "react";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { locationApi } from "../../../lib/location";
import type { UsZipLocationResponseDto } from "../../../types/types";

type UsLocationFieldsProps = {
  idPrefix: string;
  city: string;
  state: string;
  zip: string;
  required?: boolean;
  labels?: "visible" | "sr-only";
  rounded?: "md" | "xl";
  cityPlaceholder?: string;
  zipPlaceholder?: string;
  onCityChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onZipChange: (value: string) => void;
  onZipDetailsChange?: (value: UsZipLocationResponseDto | null) => void;
};

const sanitizeZipInput = (value: string) => value.replace(/[^\d-]/g, "").slice(0, 10);

const normalizeZipForLookup = (value: string) => value.replace(/\D/g, "").slice(0, 5);

const UsLocationFields = ({
  idPrefix,
  city,
  state,
  zip,
  required = false,
  labels = "visible",
  rounded = "md",
  cityPlaceholder = "Massapequa",
  zipPlaceholder = "11758",
  onCityChange,
  onStateChange,
  onZipChange,
  onZipDetailsChange,
}: UsLocationFieldsProps) => {
  const normalizedState = state.trim().toUpperCase();
  const lookupZip = normalizeZipForLookup(zip);
  const debouncedZip = useDebouncedValue(lookupZip, 250);
  const [zipDetails, setZipDetails] = useState<UsZipLocationResponseDto | null>(null);
  const [isLoadingZipDetails, setIsLoadingZipDetails] = useState(false);

  const inputClassName = [
    "w-full border border-myteengig-border bg-white text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary",
    rounded === "xl" ? "rounded-xl px-4 py-3" : "rounded-md px-2.5 py-2 sm:px-3",
  ].join(" ");
  const labelClassName = labels === "sr-only" ? "sr-only" : `mb-${rounded === "xl" ? "2" : "1"} block text-sm font-medium text-myteengig-text-primary`;

  useEffect(() => {
    if (debouncedZip.length !== 5) {
      setZipDetails(null);
      setIsLoadingZipDetails(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingZipDetails(true);

    void locationApi
      .usZipDetails(debouncedZip)
      .then((response) => {
        if (!isCurrent) {
          return;
        }
        setZipDetails(response);
      })
      .catch(() => {
        if (!isCurrent) {
          return;
        }
        setZipDetails(null);
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingZipDetails(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [debouncedZip]);

  useEffect(() => {
    onZipDetailsChange?.(zipDetails);
  }, [onZipDetailsChange, zipDetails]);

  const resolvedCity = useMemo(() => zipDetails?.city?.trim() ?? "", [zipDetails?.city]);
  const resolvedState = useMemo(() => zipDetails?.state?.trim().toUpperCase() ?? "", [zipDetails?.state]);

  useEffect(() => {
    if (resolvedCity && city.trim() !== resolvedCity) {
      onCityChange(resolvedCity);
    }
    if (resolvedState && normalizedState !== resolvedState) {
      onStateChange(resolvedState);
    }
    if (!resolvedCity && city.trim()) {
      onCityChange("");
    }
    if (!resolvedState && normalizedState) {
      onStateChange("");
    }
  }, [city, normalizedState, onCityChange, onStateChange, resolvedCity, resolvedState]);

  const cityDisplayValue = city.trim();
  const cityPlaceholderText = lookupZip.length !== 5
    ? "Enter ZIP first"
    : isLoadingZipDetails
      ? "Looking up city..."
      : cityDisplayValue || "No city found for this ZIP code";

  const helperText = lookupZip.length !== 5
    ? "Enter a 5-digit ZIP code to fill the city automatically."
    : isLoadingZipDetails
      ? "Looking up the default city for this ZIP code."
      : cityDisplayValue
        ? ""
        : "We could not resolve a city for this ZIP code.";

  return (
    <div className="grid gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
      <div>
        <label htmlFor={`${idPrefix}-zip`} className={labelClassName}>
          ZIP code
        </label>
        <input
          id={`${idPrefix}-zip`}
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          required={required}
          maxLength={10}
          value={zip}
          onChange={(event) => {
            onZipChange(sanitizeZipInput(event.target.value));
            onCityChange("");
            onStateChange("");
            setZipDetails(null);
          }}
          className={inputClassName}
          placeholder={zipPlaceholder}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-city`} className={labelClassName}>
          City
        </label>
        <input
          id={`${idPrefix}-city`}
          type="text"
          readOnly
          aria-required={required}
          value={cityDisplayValue}
          className={[
            inputClassName,
            "cursor-default bg-slate-50 read-only:text-myteengig-text-primary",
            !cityDisplayValue ? "text-myteengig-text-muted" : "",
          ].join(" ")}
          placeholder={cityDisplayValue ? cityPlaceholder : cityPlaceholderText}
        />
        {helperText ? (
          <p className="mt-1 text-xs text-myteengig-text-muted">{helperText}</p>
        ) : null}
      </div>

      <input id={`${idPrefix}-state`} type="hidden" value={normalizedState} readOnly />
    </div>
  );
};

export default UsLocationFields;
